import { Component, inject, signal, computed, HostListener } from '@angular/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { AuthService } from '../../../auth/services/auth.service';
import { UserRole } from '../../../auth/models/user-roles';
import { DialogService } from '../../../dialog/services/dialog.service';
import { TenantService } from '../../../tenant/services/tenant.service';
import { FeatureFlagService } from '../../../feature-flag/services/feature-flag.service';
import { FeatureFlag } from '../../../feature-flag/feature-flag.type';
import { environment } from '../../../../environments/environment';
import { ITenantDTO } from '../../../shared/models/tenant';

export interface NavItem {
  label: string;
  route?: string;
  icon: string;
  roles: UserRole[];
  requiresAperioSupport?: boolean;
  children?: NavItem[];
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

type TopbarMenu = 'tenant' | 'dev' | 'user';
type TopbarSubmenu = 'tenant' | 'roles' | 'flags';

@Component({
  selector: 'app-admin-layout',
  templateUrl: './admin-layout.component.html',
  styleUrls: ['./admin-layout.component.scss'],
  standalone: false
})
export class AdminLayoutComponent {
  private readonly authService = inject(AuthService);
  private readonly dialogService = inject(DialogService);
  private readonly router = inject(Router);
  readonly tenantService = inject(TenantService);
  private readonly featureFlagService = inject(FeatureFlagService);

  private windowWidth = signal(typeof window !== 'undefined' ? window.innerWidth : 1024);
  isMobile = computed(() => this.windowWidth() < 768);

  sidebarOpen = signal(true);
  expandedSections = signal<Set<string>>(new Set());
  openMenu = signal<TopbarMenu | null>(null);
  openSubmenu = signal<TopbarSubmenu | null>(null);

  shouldShowBackdrop = computed(() => this.isMobile() && this.sidebarOpen());

  readonly inDevelopment = !environment.production;
  readonly UserRole = UserRole;
  readonly dropdownPositions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 6,
    },
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 6,
    },
  ];

  get userData() { return this.authService.userData; }

  currentTenant(): ITenantDTO | null {
    return this.tenantService.tenantDetails();
  }

  currentTenantCode(): string {
    return this.tenantService.tenantCode() || 'No Tenant';
  }

  currentTenantDisplayName(): string {
    return this.currentTenant()?.name || this.tenantService.tenantCode() || 'No Tenant';
  }

  availableTenants(): ITenantDTO[] {
    return this.userData?.tenants || [];
  }

  get userInitials(): string {
    const name = this.authService.userData?.name ?? '';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  }

  readonly featureFlags = [
    { name: 'Full locker audit fields', value: FeatureFlag.ShowAllLockerAuditFields },
    { name: 'Full locker status fields', value: FeatureFlag.ShowAllLockerStatusFields },
    { name: 'Disable creating shift locker banks', value: FeatureFlag.DisableShiftLockerBanks },
    { name: 'New UI', value: FeatureFlag.UseNewAdminLayout },
  ];

  constructor() {
    if (typeof window !== 'undefined') {
      this.sidebarOpen.set(window.innerWidth >= 768);
    }

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.closeMenus();
      if (this.isMobile()) {
        this.sidebarOpen.set(false);
      }
    });
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.windowWidth.set((event.target as Window).innerWidth);
  }

  toggleMenu(menu: TopbarMenu): void {
    this.openMenu.set(this.openMenu() === menu ? null : menu);
    this.openSubmenu.set(null);
  }

  closeMenus(): void {
    this.openMenu.set(null);
    this.openSubmenu.set(null);
  }

  clearSubmenu(): void {
    this.openSubmenu.set(null);
  }

  canSwitchTenants(): boolean {
    if (!this.userData) return false;

    if (this.isSuperUser()) {
      return true;
    }

    return this.availableTenants().length > 1;
  }

  isSuperUser(): boolean {
    return this.authService.userHasRoleAndRoleIsEnabled(UserRole.SuperUser);
  }

  switchToTenant(tenant: ITenantDTO): void {
    if (!tenant.shortCode) return;

    this.closeMenus();
    this.tenantService.switchToTenant(tenant.shortCode).subscribe(() => {
      this.router.navigate([tenant.shortCode, 'admin']);
    });
  }

  goToTenantSettings(): void {
    const currentCode = this.tenantService.tenantCode();
    if (!currentCode) return;

    this.closeMenus();
    this.router.navigate([currentCode, 'admin', 'tenants']);
  }

  isFeatureFlagSet(flag: FeatureFlag): boolean {
    return this.featureFlagService.hasFeatureFlagSet(flag);
  }

  toggleFeatureFlag(flag: FeatureFlag): void {
    this.closeMenus();
    this.featureFlagService.toggleFeatureFlag(flag);
    if (flag === FeatureFlag.UseNewAdminLayout) {
      const currentUrl = this.router.url;
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigateByUrl(currentUrl);
      });
    }
  }

  private readonly allSections: NavSection[] = [
    {
      label: 'Overview',
      items: [
        { label: 'Dashboard', route: '', icon: 'home', roles: [UserRole.SuperUser, UserRole.TenantAdmin, UserRole.LockerBankAdmin, UserRole.Installer] },
        { label: 'Tenants', route: 'tenants', icon: 'business', roles: [UserRole.SuperUser] },
      ]
    },
    {
      label: 'Hardware',
      items: [
        { label: 'Locks', route: 'locks', icon: 'lock', roles: [UserRole.SuperUser, UserRole.TenantAdmin] },
        { label: 'Site Locker Controllers', route: 'slcs', icon: 'devices', roles: [UserRole.SuperUser, UserRole.TenantAdmin], requiresAperioSupport: true },
      ]
    },
    {
      label: 'Access',
      items: [
        { label: 'Lockers', route: 'locker-banks-and-lockers', icon: 'location_city', roles: [UserRole.SuperUser, UserRole.TenantAdmin] },
        { label: 'Locker Banks', route: 'locker-banks-and-lockers', icon: 'location_city', roles: [UserRole.SuperUser, UserRole.TenantAdmin] },
        { label: 'Locations', route: 'locker-banks-and-lockers', icon: 'location_on', roles: [UserRole.SuperUser, UserRole.TenantAdmin] },
        { label: 'Locker Statuses', route: 'locker-statuses-and-audits', icon: 'storage', roles: [UserRole.SuperUser, UserRole.TenantAdmin] },
        { label: 'Staff & Credentials', route: 'card-holders-and-credentials', icon: 'badge', roles: [UserRole.SuperUser, UserRole.TenantAdmin] },
      ]
    },
    {
      label: 'Card Kiosks',
      items: [
        { label: 'Card Templates', route: 'card-templates', icon: 'badge', roles: [UserRole.SuperUser] },
        { label: 'Kiosks', route: 'kiosk-groups', icon: 'desktop_windows', roles: [UserRole.SuperUser, UserRole.TenantAdmin] },
      ]
    },
    {
      label: 'System',
      items: [
        { label: 'System Notifications', route: 'system-notification', icon: 'notifications', roles: [UserRole.SuperUser] },
        {
          label: 'Reports',
          icon: 'assessment',
          roles: [UserRole.SuperUser, UserRole.TenantAdmin],
          children: [
            { label: 'System Metrics', route: 'metrics', icon: 'dashboard', roles: [UserRole.SuperUser, UserRole.TenantAdmin] },
            { label: 'Locker Report', route: 'metrics/installers', icon: 'construction', roles: [UserRole.SuperUser] },
          ]
        },
      ]
    },
  ];

  get visibleSections(): NavSection[] {
    const userData = this.authService.userData;
    if (!userData) return [];

    const filterItems = (items: NavItem[]): NavItem[] =>
      items
        .filter(item => this.canShowNavItem(item, userData.roles ?? []))
        .map(item => {
          if (item.children) {
            return { ...item, children: filterItems(item.children) };
          }
          return item;
        })
        .filter(item => !item.children || item.children.length > 0);

    return this.allSections
      .map(section => ({ ...section, items: filterItems(section.items) }))
      .filter(section => section.items.length > 0);
  }

  private canShowNavItem(item: NavItem, roles: UserRole[]): boolean {
    const hasRequiredRole = item.roles.some(role => roles.includes(role) && this.authService.isRoleEnabled(role));
    if (!hasRequiredRole) return false;

    if (!item.requiresAperioSupport) return true;

    const capabilities = this.tenantService.tenantCapabilities();
    return !!capabilities?.hasSlcSupport || !!capabilities?.hasAssaAbloyLocks;
  }

  hasRole(role: UserRole): boolean {
    return this.authService.userData?.roles?.includes(role) ?? false;
  }

  isRoleEnabled(role: UserRole): boolean {
    return this.authService.isRoleEnabled(role);
  }

  toggleRole(role: UserRole): void {
    this.closeMenus();
    this.authService.toggleRoleEnabled(role);
  }

  toggleSidebar(): void {
    this.sidebarOpen.set(!this.sidebarOpen());
  }

  toggleExpanded(item: NavItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const expanded = new Set(this.expandedSections());
    if (expanded.has(item.label)) {
      expanded.delete(item.label);
    } else {
      expanded.add(item.label);
    }
    this.expandedSections.set(expanded);
  }

  isExpanded(item: NavItem): boolean {
    return this.expandedSections().has(item.label);
  }

  showAppInfo(): void {
    const currentYear = new Date().getFullYear();
    this.dialogService.alert(
      `App Version: ${environment.appVersion}`,
      `© ${currentYear} - STSL - All rights reserved`
    );
  }

  logout(): void {
    this.closeMenus();
    this.authService.logoffAndRevokeTokens();
  }
}
